const muiCustomStyles = {
  dialogStyle: {
    "& .MuiDialog-paper": {
      borderRadius: { xs: "0px", sm: "16px" },
      maxWidth: { xs: "100%", sm: "1100px" },
      width: "100%",
      height: { xs: "100%", sm: "min(660px, 92vh)" },
      overflow: "hidden",
    },
  },

  dialogBox: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 1)",
  },

  headerBox: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: { xs: "16px", sm: "16px 24px" },
    borderBottom: "1px solid rgba(234, 234, 234, 1)",
    gap: "16px",
    minHeight: "64px",
  },

  headerCenter: {
    flex: 1,
    display: { xs: "none", md: "flex" },
    justifyContent: "center",
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  bodyBox: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    p: { xs: "16px", sm: "32px 24px 24px 24px" },
  },

  bodyInner: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    maxWidth: "560px",
    margin: "0 auto",
    width: "100%",
    gap: "32px",
  },

  // Fills the body while the offer loads. The wizard's steps are derived from
  // the fetched lists, so there is nothing meaningful to draw until they land.
  loadingBox: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  heading: {
    color: "rgba(62, 63, 76, 1)",
    fontSize: "24px",
    fontWeight: 700,
    lineHeight: "1.3",
    textAlign: "center",
    maxWidth: "420px",
    margin: "0 auto",
  },

  scrollListWrapper: {
    flex: "1 1 auto",
    minHeight: 0,
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },

  // Column headers pinned above the scrolling list so the supplier can see
  // what each column is for (which items to pick vs. the community discount).
  // The header mirrors a data row's column geometry so each label sits above
  // its column: the selection label starts where the row's checkbox label
  // starts, and the discount label ends where the row's discount input ends
  // (accounting for the list's 12px scroll gutter on the end/left side).
  // NOTE: this app flips physical padding in RTL (stylis-plugin-rtl), so `pl`
  // here renders as physical padding-right (the selection/start side) and `pr`
  // renders as physical padding-left (the discount/end side).
  listHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    // pr -> physical left inset: pushes the discount label right so it ends
    // flush with the row edge instead of the scrollbar.
    pr: "28px",
    // pl -> physical right inset: keeps the selection label aligned with the row.
    pl: "12px",
    pb: "12px",
  },

  listHeaderSelection: {
    flex: 1,
    minWidth: 0,
    color: "rgba(62, 63, 76, 0.6)",
    fontSize: "13px",
    fontWeight: 500,
  },

  listHeaderDiscount: {
    flexShrink: 0,
    whiteSpace: "nowrap",
    color: "rgba(62, 63, 76, 0.6)",
    fontSize: "13px",
    fontWeight: 500,
  },

  scrollList: {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflowY: "auto",
    paddingRight: "12px",
    paddingBottom: "8px",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(120, 120, 120, 0.5)",
      borderRadius: "2px",
    },
  },

  scrollFade: {
    position: "absolute",
    left: 0,
    right: "12px",
    bottom: 0,
    height: "48px",
    pointerEvents: "none",
    background:
      "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.95) 100%)",
  },

  pricelistRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px solid rgba(234, 234, 234, 1)",
    borderRadius: "16px",
    padding: "8px 12px 8px 8px",
    backgroundColor: "rgba(255, 255, 255, 1)",
  },

  currentActiveChip: {
    backgroundColor: "rgba(36, 176, 98, 0.16)",
    padding: "4px 10px",
    borderRadius: "50px",
    flexShrink: 0,
  },

  currentActiveChipText: {
    color: "rgba(36, 176, 98, 1)",
    fontWeight: 500,
    fontSize: "13px",
    lineHeight: "22px",
    whiteSpace: "nowrap",
  },

  pricelistLabel: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    "& .MuiFormControlLabel-root": {
      minWidth: 0,
      width: "100%",
    },
    "& .MuiFormControlLabel-label": {
      minWidth: 0,
      flex: 1,
    },
  },

  discountInputBox: {
    display: "flex",
    alignItems: "center",
    border: "1px solid rgba(234, 234, 234, 1)",
    borderRadius: "12px",
    px: "14px",
    py: "8px",
    minWidth: "100px",
    height: "40px",
    justifyContent: "flex-end",
    transition: "opacity 120ms ease, border-color 120ms ease",
    backgroundColor: "rgba(255, 255, 255, 1)",
  },

  discountTextField: {
    width: "44px",
    "& .MuiInputBase-root": {
      padding: 0,
    },
    "& .MuiInputBase-input": {
      padding: "0px",
      fontSize: "15px",
      fontWeight: 500,
      color: "rgba(62, 63, 76, 1)",
      textAlign: "right",
      "&::placeholder": {
        color: "rgba(62, 63, 76, 0.4)",
        opacity: 1,
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },

  discountPercentSign: {
    color: "rgba(62, 63, 76, 1)",
    fontSize: "15px",
    fontWeight: 500,
    ml: "2px",
  },

  footerBox: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: { xs: "12px 16px", sm: "16px 24px" },
    borderTop: "1px solid rgba(234, 234, 234, 1)",
    gap: "16px",
    flexWrap: "wrap",
    backgroundColor: "rgba(255, 255, 255, 1)",
  },

  // Mobile home for the stepper. The header only has room for it from md up
  // (see `headerCenter`), so below that breakpoint it rides in the footer on
  // its own full-width row *above* the Back/Next buttons — never between them —
  // so the supplier can still see which step they are on.
  footerStepper: {
    display: { xs: "flex", md: "none" },
    width: "100%",
    justifyContent: "center",
    pb: "4px",
  },

  termsRow: {
    display: "flex",
    alignItems: "center",
    // Below md the footer stacks (consent, stepper, buttons). Claiming the whole
    // row here rather than relying on the consent text being long enough to wrap
    // is what keeps the stepper on its own line directly above the buttons.
    flexBasis: { xs: "100%", md: "auto" },
    "& .MuiFormControlLabel-root": { ml: "-7px" },
  },

  termsText: {
    color: "rgba(62, 63, 76, 1)",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
  },

  termsLink: {
    fontWeight: 400,
    textDecoration: "underline",
    cursor: "pointer",
  },

  footerActions: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginLeft: "auto",
  },

  cancelOfferText: {
    fontSize: "13px",
    fontWeight: 400,
    textDecoration: "underline",
    cursor: "pointer",
  },

  actionButtonStyle: {
    backgroundColor: "rgba(37, 124, 109, 1)",
    color: "white",
    fontSize: 15,
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "8px",
    paddingX: "12px",
    paddingY: "8px",
    height: "39px",
    boxShadow: "none !important",
    "&:hover": { backgroundColor: "rgba(31, 106, 90, 1)" },
    "&.Mui-disabled": {
      backgroundColor: "rgba(37, 124, 109, 0.4)",
      color: "rgba(255, 255, 255, 1)",
    },
  },
};

export default muiCustomStyles;
